import type {
  ExportTimeLogsParams,
  KanbanTask,
  KanbanTaskChange,
  KanbanTaskComment,
  KanbanTaskStatus,
  NewKanbanTask,
  NewKanbanTaskComment,
} from '../KanbanTaskStores';
import type { Attachment, ScheduledAndPriorityUpdate, TaskTimeLog, TaskTimeMetrics } from '../../api/KanbanTaskApiClient';

/**
 * Contract of the one board store (`KanbanTaskStore`) as the board page, the list view and the
 * task drawer see it. Since Task 9 the same store serves the authenticated and the public board
 * (`isAnonymous` follows `KanbanTaskStore.setMode`), so every member is required: the public
 * board has parity (spec decision 4) and the server answers 401/404 where a board token may not go.
 *
 * Method/property types are copied verbatim from `KanbanTaskStore` so the compile-time check in its
 * constructor keeps holding.
 */
export interface TaskStore {
  readonly isAnonymous: boolean;
  readonly isLoading: boolean;

  /** Synchronous lookup in whatever the store already has loaded. */
  getTask(id: string): KanbanTask | undefined;
  /** Fetches the full task from the server and upserts it into the store's local state. */
  fetchTask(idOrPublicId: string): Promise<KanbanTask | undefined>;

  readonly projectStatuses: KanbanTaskStatus[];
  /** Every loaded, not deleted task sorted by column then index (list view). */
  readonly projectTasks: KanbanTask[];
  /** `projectTasks` filtered by `boardRules.isVisibleOnBoard` (the board). */
  readonly kanbanViewTasks: KanbanTask[];
  readonly deletedProjectTasks: KanbanTask[];
  /** Attachments of the currently open task. */
  readonly attachments: Attachment[];
  /** True while the attachment list of the open task is being read; the drawer shows placeholders. */
  readonly attachmentsLoading: boolean;
  /** Change history of the currently open task. */
  readonly taskChanges: KanbanTaskChange[];
  readonly taskTimeMetrics: TaskTimeMetrics | null;
  readonly taskTimeLogs: TaskTimeLog[];
  readonly projectCustomFields: any[];
  readonly taskCustomFieldValues: Record<string, any[]>;

  getProjectStatuses(projectId: string): Promise<void>;
  getProjectTasks(projectId: string): Promise<void>;
  getDeletedProjectTasks(projectId: string): Promise<void>;
  /** Loads the task row, changes, attachments, time and custom fields — what the drawer loads on open. Comments are fetched by TaskComments itself. */
  loadTaskDetails(taskId: string): Promise<void>;
  getTaskComments(taskId: string): Promise<KanbanTaskComment[]>;
  createComment(comment: NewKanbanTaskComment): Promise<KanbanTaskComment | null>;

  createTask(task: NewKanbanTask): Promise<KanbanTask | null>;
  updateTaskTitle(taskId: string, title: string): Promise<void>;
  updateTaskDescription(taskId: string, html: string): Promise<void>;
  updateTaskStatus(taskId: string, statusId: string): Promise<void>;
  updateTaskScheduledAndPriority(taskId: string, scheduledAndPriority: ScheduledAndPriorityUpdate): Promise<void>;
  updateTaskAssignee(taskId: string, assigneeId?: string): Promise<void>;
  moveTask(taskId: string, toStatusId: string, toIndex: number): Promise<void>;
  updateTaskOrder(taskId: string, newIndex: number): Promise<void>;

  uploadTaskImage(taskId: string, file: File): Promise<string>;
  uploadTaskAttachment(taskId: string, file: File, confirmation?: boolean): Promise<Attachment | null>;
  deleteAttachment(publicId: string, taskId: string): Promise<boolean>;

  logTimeForTask(taskId: string, time: string, description: string, userId?: string, endDate?: string): Promise<boolean>;
  updateEstimatedTime(taskId: string, estimatedTime: string): Promise<boolean>;

  setTaskCustomFieldValue(taskId: string, fieldId: string, value: string, userId?: string): Promise<boolean>;
  getProjectCustomFields(projectId: string): Promise<void>;
  getTaskCustomFieldValues(taskId: string): Promise<void>;
  readonly isCustomFieldsLoading: boolean;
  /** Synchronous lookup in `taskCustomFieldValues`; null when the task has no value for the field. */
  getFieldValueForTask(taskId: string, fieldId: string): any;

  deleteTask(taskId: string): Promise<void>;
  restoreTask(taskId: string): Promise<void>;
  deleteTaskPermanent(taskId: string): Promise<void>;
  bulkRestore(taskIds: string[]): Promise<unknown>;
  bulkSoftDelete(taskIds: string[]): Promise<unknown>;
  bulkPermanentDelete(taskIds: string[]): Promise<unknown>;

  createStatus(name: string, projectId: string, isFinalStatus: boolean): Promise<KanbanTaskStatus | null>;
  updateStatus(statusId: string, name: string, isFinalStatus: boolean): Promise<KanbanTaskStatus | null>;
  updateStatusOrder(statusId: string, newIndex: number): Promise<void>;
  /** Resolves `true` on success; `moveTasksTo` names the column that receives the tasks (server Task 6). */
  deleteStatus(statusId: number, moveTasksTo?: number): Promise<boolean>;

  exportTimeLogsAndDownload(params: ExportTimeLogsParams): Promise<boolean>;
}

/**
 * True when a save was rejected because the content still carried an inline (base64) image.
 * The server contract (Task 2) returns `400 { code: "InlineImagesNotAllowed" }` for any save
 * of rich text that still embeds a data: URI image instead of an uploaded one.
 */
export function isInlineImagesNotAllowedError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: string } } } | null | undefined)?.response;
  return response?.status === 400 && response?.data?.code === 'InlineImagesNotAllowed';
}
